import type { ProfileCategoryCountsResponse, ProfileSort } from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { FilterGroup } from '@/types/filters';

import type {
  AiSearchResult,
  FetchProfilesParams,
  MyProfilesGateway,
  ProfilesPage,
} from '../infrastructure/my-profiles.gateway';
import {
  aiSearchRequested,
  categoryCountsRequested,
  facetOptionsRequested,
  filtersChanged,
  myProfilesRequested,
  pageChanged,
  pageSizeChanged,
  searchChanged,
  sortChanged,
} from './my-profiles.actions';
import {
  selectFilters,
  selectLicenceId,
  selectPage,
  selectPageSize,
  selectSearch,
  selectSort,
} from './my-profiles.selectors';
import { myProfilesActions } from './my-profiles.slice';

const LOAD_ERROR = 'Failed to load profiles.';
const AI_SEARCH_ERROR = 'The AI search failed. Try rephrasing, or add the filters yourself.';

/** Best-effort human message for the failure banner (no domain error union). */
function toMessage(e: unknown, fallback: string): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * WORKER: fetch the current page. Reads `page` / `pageSize` / `filters` / `sort` back from
 * the store (the single source of truth for the fetch) and calls the gateway.
 * `takeLatest` cancels an in-flight fetch when a newer trigger arrives; the
 * abort-latest behaviour the old `useInfiniteApi` had.
 */
function* fetchCurrentPage() {
  // No licence yet, no request (PI-117): for an admin, a page fetched without
  // it holds every licence, and would flash on screen before the right one.
  const licenceId: unknown = yield select(selectLicenceId);
  if (typeof licenceId !== 'string') return;

  const gateway: MyProfilesGateway = yield getContext('myProfilesGateway');
  const page: number = yield select(selectPage);
  const pageSize: number = yield select(selectPageSize);
  const filters: FilterGroup | null = yield select(selectFilters);
  // `unknown`, not `string`: every typed yield here is intersected into the
  // generator's `next` type, and `string & number` would make it `never`,
  // which the workers delegating with `yield*` then cannot feed.
  const search: unknown = yield select(selectSearch);
  const sort: ProfileSort | null = yield select(selectSort);

  yield put(myProfilesActions.loadStarted());
  try {
    const params: FetchProfilesParams = { filters, page, pageSize, licenceId, sort };
    if (typeof search === 'string' && search) params.q = search;
    const result = (yield call(gateway.fetchProfiles, params)) as ProfilesPage;
    yield put(myProfilesActions.loadSucceeded(result));
  } catch (e) {
    yield put(myProfilesActions.loadFailed(toMessage(e, LOAD_ERROR)));
  }
}

/** WORKER: apply new server filters (resets to page 0), then fetch. */
function* filtersChangedWorker(action: ReturnType<typeof filtersChanged>) {
  yield put(myProfilesActions.filtersSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: apply a new keyword search (resets to page 0), then fetch. */
function* searchChangedWorker(action: ReturnType<typeof searchChanged>) {
  yield put(myProfilesActions.searchSet(action.payload.trim()));
  yield* fetchCurrentPage();
}

/** WORKER: apply a new sort (resets to page 0), then fetch. */
function* sortChangedWorker(action: ReturnType<typeof sortChanged>) {
  yield put(myProfilesActions.sortSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: move to a page, then fetch it. */
function* pageChangedWorker(action: ReturnType<typeof pageChanged>) {
  yield put(myProfilesActions.pageSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: change the page size (resets to page 0), then fetch. */
function* pageSizeChangedWorker(action: ReturnType<typeof pageSizeChanged>) {
  yield put(myProfilesActions.pageSizeSet(action.payload));
  yield* fetchCurrentPage();
}

/**
 * WORKER: the category band's figures (#553).
 *
 * Its own fetch, not folded into the page load: the figures describe the
 * categories, not the current page or the current filter, so they must not be
 * refetched every time the cursor moves.
 *
 * A failure clears the band and stays silent. It is a header over a table that
 * loaded fine, and a snackbar for it would fire on every visit while the
 * endpoint is down; the table below already carries the page's real message.
 */
function* categoryCountsWorker() {
  const gateway: MyProfilesGateway = yield getContext('myProfilesGateway');
  try {
    const counts: ProfileCategoryCountsResponse = yield call(gateway.fetchCategoryCounts);
    yield put(myProfilesActions.categoryCountsLoaded(counts));
  } catch {
    yield put(myProfilesActions.categoryCountsCleared());
  }
}

/**
 * WORKER: the Country and Custom tags facets' choices (#615).
 *
 * Each list fails on its own and falls back to empty: the facet then shows no
 * choice, and the rest of the rail (and the table) keep working.
 */
function* fetchOrEmpty(fetch: () => Promise<string[]>) {
  try {
    const values: string[] = yield call(fetch);
    return values;
  } catch {
    return [] as string[];
  }
}

function* facetOptionsWorker() {
  const gateway: MyProfilesGateway = yield getContext('myProfilesGateway');
  const [countries, tags]: [string[], string[]] = yield all([
    call(fetchOrEmpty, gateway.fetchCountries),
    call(fetchOrEmpty, gateway.fetchTagSuggestions),
  ]);
  yield put(myProfilesActions.facetOptionsLoaded({ countries, tags }));
}

/**
 * WORKER: "Ask AI" (#567). Only asks: the rules land in the page's editor,
 * whose settled tree then drives the fetch like any other edit.
 */
function* aiSearchWorker(action: ReturnType<typeof aiSearchRequested>) {
  const query = action.payload.trim();
  if (!query) return;
  const gateway: MyProfilesGateway = yield getContext('myProfilesGateway');
  yield put(myProfilesActions.aiSearchStarted());
  try {
    const result: AiSearchResult = yield call(gateway.interpretSearch, query);
    yield put(myProfilesActions.aiSearchAnswered({ ...result, query }));
  } catch (e) {
    yield put(myProfilesActions.aiSearchFailed(toMessage(e, AI_SEARCH_ERROR)));
  }
}

/** WATCHERS. */
export function* myProfilesSaga() {
  yield all([
    takeLatest(myProfilesRequested.type, fetchCurrentPage),
    takeLatest(categoryCountsRequested.type, categoryCountsWorker),
    takeLatest(facetOptionsRequested.type, facetOptionsWorker),
    takeLatest(filtersChanged.type, filtersChangedWorker),
    takeLatest(searchChanged.type, searchChangedWorker),
    takeLatest(sortChanged.type, sortChangedWorker),
    takeLatest(aiSearchRequested.type, aiSearchWorker),
    takeLatest(pageChanged.type, pageChangedWorker),
    takeLatest(pageSizeChanged.type, pageSizeChangedWorker),
  ]);
}
